import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Check for existing token on app load
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user_profile');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
      
      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      
      // Verify token is still valid
      verifyToken(savedToken);
    } else {
      // Check if user has session cookie (Google OAuth)
      verifySession();
    }
  }, []);

  const verifySession = async () => {
    try {
      // Check if user has valid session cookie (Google OAuth)
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        withCredentials: true // Send cookies
      });

      if (response.data.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.log('No active session found');
      setLoading(false);
    }
  };

  const verifyToken = async (authToken) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.data.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        // Token is invalid, clear auth data
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      console.log('🔄 Attempting login for:', username);
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        username,
        password
      });
      console.log('🔐 Login response:', response.data);

      if (response.data.success) {
        const { user: userData, access_token } = response.data;
        
        console.log('✅ Login successful, setting auth state');
        console.log('👤 User data:', userData);
        console.log('🔑 Token received:', access_token ? 'Yes' : 'No');
        
        // Save to state
        setUser(userData);
        setToken(access_token);
        setIsAuthenticated(true);

        // Save to localStorage
        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('user_profile', JSON.stringify(userData));

        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        console.log('🔐 Authorization header set, isAuthenticated should be true');

        return { success: true, user: userData };
      } else {
        console.error('❌ Login failed:', response.data.error);
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('📡 Response data:', error.response?.data);
      const errorMessage = error.response?.data?.error || 'Login failed. Please try again.';
      return { success: false, error: errorMessage };
    }
  };

  const register = async (registrationData) => {
    try {
      console.log('🔄 Attempting registration with:', registrationData.username);
      const response = await axios.post(`${API_BASE_URL}/api/auth/register`, registrationData);
      console.log('📝 Registration response:', response.data);

      if (response.data.success) {
        const { user: userData, access_token } = response.data;
        
        console.log('✅ Registration successful, setting auth state');
        console.log('👤 User data:', userData);
        console.log('🔑 Token received:', access_token ? 'Yes' : 'No');
        
        // Save to state
        setUser(userData);
        setToken(access_token);
        setIsAuthenticated(true);

        // Save to localStorage
        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('user_profile', JSON.stringify(userData));

        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        console.log('🔐 Authorization header set');

        return { success: true, user: userData };
      } else {
        console.error('❌ Registration failed:', response.data.error);
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      console.error('📡 Response data:', error.response?.data);
      const errorMessage = error.response?.data?.error || 'Registration failed. Please try again.';
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    // Clear state
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);

    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_profile');

    // Remove authorization header
    delete axios.defaults.headers.common['Authorization'];

    // Call backend logout endpoint to clear session cookie (Google OAuth)
    axios.post(`${API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true })
      .catch(err => console.error('Logout cleanup failed:', err));
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/auth/profile`, profileData);

      if (response.data.success) {
        const updatedUser = response.data.user;
        setUser(updatedUser);
        localStorage.setItem('user_profile', JSON.stringify(updatedUser));
        return { success: true, user: updatedUser };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      const errorMessage = error.response?.data?.error || 'Profile update failed. Please try again.';
      return { success: false, error: errorMessage };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });

      if (response.data.success) {
        return { success: true };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Password change failed:', error);
      const errorMessage = error.response?.data?.error || 'Password change failed. Please try again.';
      return { success: false, error: errorMessage };
    }
  };

  const generateSankalpa = async (dateStr = null) => {
    try {
      const endpoint = dateStr 
        ? `${API_BASE_URL}/api/devimana/sankalpa/date`
        : `${API_BASE_URL}/api/devimana/sankalpa/current`;

      const payload = dateStr ? { date: dateStr } : {};

      const response = await axios.post(endpoint, payload);

      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Sankalpa generation failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed to generate Sankalpa. Please try again.';
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    generateSankalpa
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};